import { Head, router } from '@inertiajs/react'
import { BarChart3, TrendingDown, TrendingUp } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import NavigationRail from '@/components/NavigationRail'
import Avatar from '@/components/Avatar'
import { formatDuration } from '@/lib/displayUtils'
import type { Agent } from '@/types/shared'

interface Kpi {
  value: number | null
  deltaPct: number | null
}

interface TrendPoint {
  date: string
  created: number
}

interface AgentRow {
  id: number
  name: string
  avatarUrl: string | null
  assigned: number
  closed: number
  messagesSent: number
  medianFirstResponseSeconds: number | null
  averageRating: number | null
}

interface ReportsIndexProps {
  currentAgent: Agent
  filters: { period: string; channel: string; businessHours: boolean }
  businessHoursConfigured: boolean
  kpis: {
    newConversations: Kpi
    closedConversations: Kpi
    medianFirstResponseSeconds: Kpi
    p90FirstResponseSeconds: Kpi
    medianReplyTimeSeconds: Kpi
    medianResolutionSeconds: Kpi
    openBacklog: Kpi
  }
  trend: TrendPoint[]
  heatmap: number[][]
  agents: AgentRow[]
}

const PERIODS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
]

const CHANNELS = [
  { value: 'all', label: 'Todos os canais' },
  { value: 'chat', label: 'Chat' },
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
]

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function formatShortDate(iso: string): string {
  const [, month, day] = iso.split('-')
  return `${day}/${month}`
}

// For durations a drop is an improvement; invert the color semantics.
function DeltaBadge({ deltaPct, lowerIsBetter = false }: { deltaPct: number | null; lowerIsBetter?: boolean }) {
  if (deltaPct === null) return null
  const isUp = deltaPct > 0
  const isGood = lowerIsBetter ? deltaPct < 0 : deltaPct > 0
  const Icon = isUp ? TrendingUp : TrendingDown
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        deltaPct === 0 ? 'text-gray-400' : isGood ? 'text-emerald-600' : 'text-red-600'
      }`}
      title="Variação em relação ao período anterior"
    >
      <Icon className="w-3 h-3" />
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  )
}

// duration also implies "a drop is good": every duration KPI is a wait time.
function KpiCard({ label, kpi, duration = false }: { label: string; kpi: Kpi; duration?: boolean }) {
  const display = duration ? formatDuration(kpi.value) : (kpi.value ?? '—')
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900">{display}</span>
        <DeltaBadge deltaPct={kpi.deltaPct} lowerIsBetter={duration} />
      </div>
    </div>
  )
}

function Heatmap({ matrix }: { matrix: number[][] }) {
  const max = Math.max(1, ...matrix.flat())
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        <div className="grid grid-cols-[40px_repeat(24,1fr)] gap-0.5 text-[10px] text-gray-400">
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="text-center">
              {h % 3 === 0 ? `${h}h` : ''}
            </div>
          ))}
        </div>
        {matrix.map((row, dow) => (
          <div key={dow} className="grid grid-cols-[40px_repeat(24,1fr)] gap-0.5 mt-0.5">
            <div className="text-xs text-gray-500 flex items-center">{WEEKDAYS[dow]}</div>
            {row.map((count, hour) => (
              <div
                key={hour}
                title={`${WEEKDAYS[dow]} ${hour}h — ${count} conversa${count === 1 ? '' : 's'}`}
                className="h-5 rounded-sm bg-indigo-600"
                style={{ opacity: count === 0 ? 0.06 : 0.15 + (count / max) * 0.85 }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ReportsIndex({
  currentAgent,
  filters,
  businessHoursConfigured,
  kpis,
  trend,
  heatmap,
  agents,
}: ReportsIndexProps) {
  const applyFilters = (changes: Partial<typeof filters>) => {
    const next = { ...filters, ...changes }
    router.get(
      '/reports',
      { period: next.period, channel: next.channel, business_hours: next.businessHours },
      { preserveState: true },
    )
  }

  const hasData = trend.some(point => point.created > 0)

  return (
    <>
      <Head title="Relatórios" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="reports" />

        <div className="flex-1 flex flex-col min-w-0 overflow-auto">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0">
            <h1 className="text-lg font-semibold text-gray-900">Relatórios</h1>
          </header>

          <div className="p-6 space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => applyFilters({ period: p.value })}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors cursor-pointer ${
                    filters.period === p.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}

              <select
                value={filters.channel}
                onChange={e => applyFilters({ channel: e.target.value })}
                className="px-3 py-1.5 text-sm rounded-lg bg-white text-gray-700 border border-gray-300 cursor-pointer"
                aria-label="Canal"
              >
                {CHANNELS.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>

              {businessHoursConfigured && (
                <label className="flex items-center gap-2 text-sm text-gray-700 ml-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.businessHours}
                    onChange={e => applyFilters({ businessHours: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  Considerar só horário de atendimento
                </label>
              )}
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard label="Novas conversas" kpi={kpis.newConversations} />
              <KpiCard label="Conversas fechadas" kpi={kpis.closedConversations} />
              <KpiCard label="Conversas abertas agora" kpi={kpis.openBacklog} />
              <KpiCard label="1ª resposta (mediana)" kpi={kpis.medianFirstResponseSeconds} duration />
              <KpiCard label="1ª resposta (p90)" kpi={kpis.p90FirstResponseSeconds} duration />
              <KpiCard label="Espera por resposta (mediana)" kpi={kpis.medianReplyTimeSeconds} duration />
              <KpiCard label="Tempo de resolução (mediana)" kpi={kpis.medianResolutionSeconds} duration />
            </div>

            {/* Trend chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Conversas por dia</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatShortDate}
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickLine={false}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                    <Tooltip labelFormatter={label => formatShortDate(String(label))} />
                    <Line
                      type="monotone"
                      dataKey="created"
                      name="Criadas"
                      stroke="#4f46e5"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Day x hour heatmap */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-1">Horários de maior movimento</h2>
              <p className="text-xs text-gray-500 mb-4">
                Novas conversas por dia da semana e hora (horário de Brasília)
              </p>
              <Heatmap matrix={heatmap} />
            </div>

            {/* Agent table */}
            {agents.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-900">Desempenho por agente</h2>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-medium">Agente</th>
                      <th className="text-center px-3 py-3 font-medium">Atribuídas</th>
                      <th className="text-center px-3 py-3 font-medium">Fechadas</th>
                      <th className="text-center px-3 py-3 font-medium">Mensagens</th>
                      <th className="text-center px-3 py-3 font-medium">1ª resposta (mediana)</th>
                      <th className="text-center px-3 py-3 font-medium">Avaliação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agents.map(agent => (
                      <tr key={agent.id}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={agent.name} avatarUrl={agent.avatarUrl} size="sm" />
                            <span className="font-medium text-gray-900">{agent.name}</span>
                          </div>
                        </td>
                        <td className="text-center px-3 py-3 text-gray-600">{agent.assigned}</td>
                        <td className="text-center px-3 py-3 text-gray-600">{agent.closed}</td>
                        <td className="text-center px-3 py-3 text-gray-600">{agent.messagesSent}</td>
                        <td className="text-center px-3 py-3 text-gray-600">
                          {formatDuration(agent.medianFirstResponseSeconds)}
                        </td>
                        <td className="text-center px-3 py-3 text-gray-600">
                          {agent.averageRating !== null ? `${agent.averageRating} ★` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Empty state */}
            {!hasData && (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">Sem dados no período</h3>
                <p className="text-sm text-gray-500">
                  As estatísticas aparecerão aqui conforme as conversas forem criadas e atendidas.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
